import styled from 'styled-components'
import React, { useContext, MouseEvent } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'

import Navbar, { NavbarLinkWrapper } from 'components/Navbar'
import Tooltip from 'components/Tooltip'
import VersionLabel from 'components/VersionLabel'
import UserNavbarSection from 'modules/user/components/UserNavbarSection'
import { VersionLink, useVersioning } from 'modules/versioning'
import { ArtboardDetailInfoFragment, ShareInfoFragment } from 'gqlTypes'
import { Pagination } from '../types'
import Zoom, { ZoomProps, ZoomTypes } from 'components/Zoom'
import { LaunchSketchAlert } from 'containers/modals/LaunchSketchAlert'
import { ModalContext } from 'containers/ModalManager'
import ArtboardPaginationControls from './ArtboardPaginationControls'
import HeaderNavigation from './HeaderNavigation'
import HeaderWorkspaceBadge from './HeaderWorkspaceBadge'

import { useForTablet } from 'components/Breakpoint'

import { RouteParams } from 'constants/routes'

import { ReactComponent as SidebarIcon } from 'icons/sidebar-right-20.svg'
import { ReactComponent as ArrowLeft } from 'icons/arrow.left-20.svg'
import { ReactComponent as PlayIcon } from 'icons/play-20.svg'
import { ReactComponent as ExternalIcon } from 'icons/square.arrow.up.right-24.svg'

const NavbarButton = Navbar.Button
const NavbarDivider = Navbar.Divider
const NavbarSection = Navbar.Section

const VersionLinkLabel = VersionLabel.withComponent(VersionLink)

/**
 * TYPES
 */
type ActivityPanelToggleProps = {
  onClick: () => void
  active: boolean
}

type PlayButtonProps = {
  disabled: boolean
}

interface DetailHeaderProps {
  artboard: ArtboardDetailInfoFragment
  isViewingLatestVersion: boolean
  isPrototypePlayEnabled: boolean
  pagination: Pagination | undefined
  artboardIdAtLatestVersion: string
  activityIsOpen: boolean
  toggleActivity: () => void
  currentZoom: ZoomTypes
  onZoomChanged: ZoomProps['onZoomChanged']
  share: OmitSafe<ShareInfoFragment, 'version'>
  isSidebarLeftOpen?: boolean
  setSidebarLeftOpen?: React.Dispatch<React.SetStateAction<boolean>>
  userCanOpenInApp: boolean
}

/**
 * STYLES
 */
const NavbarSectionBackAndPages = styled(NavbarSection)``

const NavbarSectionActions: React.FC = ({ children }) => (
  <NavbarSection align="end">{children}</NavbarSection>
)

/**
 * HELPER COMPONENTS
 */
const ActivityPanelToggle = (props: ActivityPanelToggleProps) => (
  <NavbarButton size={24} icon={SidebarIcon} {...props} />
)
const PlayButton = (props: PlayButtonProps) => (
  <NavbarLinkWrapper {...props}>
    <PlayIcon height={18} width={18} />
  </NavbarLinkWrapper>
)

const CloseButton = () => {
  const history = useHistory<{ pageUUID?: string }>()
  const match = useRouteMatch<RouteParams<'SHARE_VIEW'>>()
  const { getPathname } = useVersioning()

  const pageUUID = history?.location?.state?.pageUUID

  const backPath = getPathname(
    !pageUUID
      ? {
          routeKey: 'SHARE_VIEW',
          routeParams: { shareID: match.params.shareID },
        }
      : {
          routeKey: 'SHARE_PAGE_VIEW',
          routeParams: { shareID: match.params.shareID, pageUUID },
        }
  )

  const closeDetailView = () => {
    history.push(backPath)
  }

  return (
    <NavbarButton
      icon={ArrowLeft}
      size={20}
      onClick={closeDetailView}
      data-testid="detail-header-close"
    />
  )
}

/**
 * MAIN COMPONENT
 */
const DetailHeader: React.FC<DetailHeaderProps> = ({
  artboard,
  isViewingLatestVersion,
  isPrototypePlayEnabled,
  pagination,
  artboardIdAtLatestVersion,
  activityIsOpen,
  toggleActivity,
  currentZoom,
  onZoomChanged,
  share,
  isSidebarLeftOpen,
  setSidebarLeftOpen,
  userCanOpenInApp,
}) => {
  const { showModal } = useContext(ModalContext)

  const match = useRouteMatch<RouteParams<'ARTBOARD_REVISION'>>()
  const { shareID, permanentArtboardShortId } = match.params

  const isTabletAndBigger = useForTablet()

  const shouldShowLatestUpdateBadge =
    !isViewingLatestVersion &&
    // never show if user can only access latest version (it'll update automatically)
    share.versionTypeAvailableToUser !== 'LATEST'

  const shouldShowPagination =
    Number.isInteger(pagination?.current) && Number.isInteger(pagination?.total)

  const isPublicShare =
    !share.workspace.userIsMember && !share.workspace.userIsOwner

  return (
    <Navbar isSidebarLeftOpen={isSidebarLeftOpen}>
      <NavbarSectionBackAndPages>
        {isPublicShare ? (
          <CloseButton />
        ) : (
          <HeaderWorkspaceBadge
            setSidebarLeftOpen={setSidebarLeftOpen}
            workspace={share.workspace!}
          />
        )}

        <NavbarDivider />
        {shouldShowPagination && (
          <ArtboardPaginationControls
            current={pagination!.current!}
            total={pagination!.total!}
            showNumbers={isTabletAndBigger}
          />
        )}
      </NavbarSectionBackAndPages>

      <HeaderNavigation currentView="artboard" artboard={artboard} />

      <NavbarSectionActions>
        {shouldShowLatestUpdateBadge && isTabletAndBigger && (
          <>
            <VersionLinkLabel
              mr={4}
              to={{
                routeKey: 'ARTBOARD_REVISION',
                routeParams: {
                  shareID,
                  permanentArtboardShortId,
                  revisionDocShortId: artboardIdAtLatestVersion,
                },
              }}>
              see latest update
            </VersionLinkLabel>
            <NavbarDivider />
          </>
        )}

        {artboard?.belongsToPrototype && (
          <Tooltip
            spacing="14px"
            disabled={isPrototypePlayEnabled}
            content={
              <span>
                Prototype playback is not available when browsing artboard
                versions
              </span>
            }
            placement="bottom">
            <VersionLink
              onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                if (!isPrototypePlayEnabled) event.preventDefault()
              }}
              to={{
                routeKey: 'PROTOTYPE',
                routeParams: {
                  shareID,
                  permanentArtboardShortId,
                },
              }}>
              <PlayButton disabled={!isPrototypePlayEnabled} />
            </VersionLink>
          </Tooltip>
        )}

        <Zoom zoom={currentZoom} onZoomChanged={onZoomChanged} />

        <NavbarDivider />

        {userCanOpenInApp && isTabletAndBigger && (
          <Tooltip
            content={
              isViewingLatestVersion
                ? 'Edit'
                : 'Select the latest update of this document to open it in the Mac app'
            }
            placement="bottom">
            <NavbarButton
              size={24}
              icon={ExternalIcon}
              onClick={() =>
                showModal(LaunchSketchAlert, {
                  shareId: share.identifier,
                  action: 'openInSketch',
                })
              }
              disabled={!isViewingLatestVersion}
            />
          </Tooltip>
        )}

        <ActivityPanelToggle onClick={toggleActivity} active={activityIsOpen} />

        {isTabletAndBigger && (
          <>
            <NavbarDivider />
            <UserNavbarSection />
          </>
        )}
      </NavbarSectionActions>
    </Navbar>
  )
}

export default DetailHeader
